/**
 * Access tokens: personal tokens the signed-in person pastes into an MCP client (Claude Code, a Paseo agent) so it can
 * read a founder app's screens as them. Each is for one app, shown once, and stops at its expiry or when revoked.
 * Takes the API client as a prop, so the founder side and, later, viewers and admins can each mount it.
 */
import { useState, type FormEvent } from 'react'
import type { TokenApp, TokenCreated } from '../../domain/schemas'
import { DEFAULT_EXPIRY, EXPIRY_CHOICES, expiryDays, expiryWords, lastUsedWords, pasteHint, tokenNameProblem } from '../../domain/tokens'
import type { Api } from '../../infra/api'
import { useLoad } from '../useLoad'
import { useNow } from '../useNow'
import { ConfirmAction } from './ConfirmAction'
import { SecretOnce } from './SecretOnce'
import { Empty, Field, Notice, Section, StatusChip } from './ui'

export function AccessTokens({ api }: { api: Api }) {
  const now = useNow(60_000)
  const list = useLoad(() => api.myTokens(), [api])
  const [adding, setAdding] = useState(false)
  const [created, setCreated] = useState<TokenCreated | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const tokens = list.data?.tokens ?? []
  const apps = list.data?.founders ?? []
  const live = tokens.filter((t) => !t.expired).length
  const full = list.data ? live >= list.data.limit : false

  async function revoke(id: string) {
    setBusy(id)
    setFailed(null)
    try {
      await api.revokeToken(id)
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
      list.reload()
    }
  }

  return (
    <Section
      className="flush-table"
      title="Access tokens"
      count={list.data ? tokens.length : undefined}
      action={
        !adding && !created && apps.length > 0 ? (
          <ConfirmAction label="New token" tone="ink" onConfirm={() => setAdding(true)} disabled={full} reason={full ? `${list.data?.limit} is the most; revoke one first` : null} />
        ) : undefined
      }
    >
      <p className="label fleet-sum">
        For an MCP client, like Claude Code, to read your app's screens as you. Each token is for one app, is shown once, and stops working when it expires or you revoke it.
      </p>
      {list.error && <Notice tone="bad">{list.error.message}</Notice>}
      {failed && <Notice tone="bad">{failed}</Notice>}
      {created && (
        <SecretOnce
          message={
            <>
              <b>Made {created.token.name}.</b> Copy it now. It won't be shown again.
            </>
          }
          secret={created.secret}
          hint={pasteHint(created.mcpUrl)}
          copyLabel="Copy token"
          onDone={() => setCreated(null)}
        />
      )}
      {adding && (
        <NewToken
          api={api}
          apps={apps}
          onCancel={() => setAdding(false)}
          onCreated={(c) => {
            setAdding(false)
            setCreated(c)
            list.reload()
          }}
        />
      )}
      {!list.data && !list.error && <Empty>Loading your tokens…</Empty>}
      {list.data && apps.length === 0 && <Empty>No app is shared with you yet, so there's nothing a token could reach.</Empty>}
      {list.data && apps.length > 0 && tokens.length === 0 && !adding && !created && <Empty>No tokens yet. Make one when you connect an MCP client.</Empty>}
      {tokens.length > 0 && (
        <table className="table tokens">
          <thead>
            <tr>
              <th>Token</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Last used</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className={t.expired ? 'cleared' : undefined}>
                <td data-label="Token" className="cell-main">
                  <span className="stack">
                    <b>{t.name}</b>
                    <span className="mono muted">
                      cpgp_…{t.hint}
                      {apps.length > 1 && t.founder.name ? ` · ${t.founder.name}` : ''}
                    </span>
                  </span>
                </td>
                <td data-label="Status">
                  <StatusChip status={t.expired ? 'skipped' : 'active'} label={t.expired ? 'expired' : 'active'} />
                </td>
                <td data-label="Expires">{expiryWords(t, now)}</td>
                <td data-label="Last used">{lastUsedWords(t, now)}</td>
                <td data-label="Actions" className="cell-actions">
                  <ConfirmAction
                    label="Revoke"
                    tone="ghost"
                    question={`Revoke ${t.name}? Any MCP client using it stops reaching your app at once.`}
                    yes="Revoke"
                    onConfirm={() => void revoke(t.id)}
                    disabled={busy !== null}
                    working={busy === t.id}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  )
}

function NewToken({ api, apps, onCancel, onCreated }: { api: Api; apps: TokenApp[]; onCancel: () => void; onCreated: (c: TokenCreated) => void }) {
  const [name, setName] = useState('')
  const [app, setApp] = useState(apps[0]?.id ?? '')
  const [expiry, setExpiry] = useState(DEFAULT_EXPIRY)
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const problem = tokenNameProblem(name)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (problem) return
    setSaving(true)
    setError(null)
    try {
      onCreated(await api.createToken({ name: name.trim(), founderId: app, expiresInDays: expiryDays(expiry) }))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="hook-form" onSubmit={submit} noValidate>
      <Field label="Name" hint="Where it goes, so you know which to revoke later." {...(touched && problem ? { error: problem } : {})}>
        <input className="input" type="text" maxLength={60} autoComplete="off" placeholder="Claude Code on my laptop" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      {apps.length > 1 && (
        <Field label="App">
          <select className="input" value={app} onChange={(e) => setApp(e.target.value)}>
            {apps.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Expires">
        <select className="input" value={expiry} onChange={(e) => setExpiry(e.target.value)}>
          {EXPIRY_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </Field>
      {error && <Notice tone="bad">{error}</Notice>}
      <div className="form-foot">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-ink btn-sm" disabled={saving}>
          {saving ? 'Making…' : 'Make token'}
        </button>
      </div>
    </form>
  )
}
